/**
 * One SMS-consent checkbox.
 *
 * `label` becomes the checkbox's accessible name and `description` is wired as
 * its `aria-describedby`, so the label has to be an accurate summary of what is
 * being consented to rather than a category tag — a screen-reader user hears the
 * label as the thing they are agreeing to.
 *
 * Both are plain strings, not nodes: a versioned consent record has to be
 * reconstructable as the text it was, so markup has no place in one.
 */
export interface SmsConsentCheckbox {
  /** Stable key: the React key, the state key, and the recorded box name. */
  name: string;
  /** Short bold header above the description; the checkbox's accessible name. */
  label: string;
  /** The legal consent language, versioned by the owning {@link SmsConsentSpec}. */
  description: string;
  /** Appended to the surface's `APP-COMPONENT-` analytics prefix. */
  analyticsSuffix: string;
  /**
   * The trailing run of `description` to fold behind a "more" control — the
   * carrier boilerplate ("Message frequency varies…"), which is the same on
   * every box and pushes the meaningful consent off the screen when several
   * boxes stack.
   *
   * MUST be a literal suffix of `description`: what renders always rejoins to
   * it — `` `${summary} ${detail}` === description ``, the summary being
   * trimmed of the space the two were split on — so folding changes what is
   * *shown* at a glance, never the words or the version recorded for them. A value that
   * isn't a suffix renders the whole description unfolded rather than risk
   * showing copy the version does not name — `smsConsent.test.ts` pins the
   * relationship for every shipped spec so drift fails CI, not the user.
   *
   * Omit for a description with no boilerplate to fold (reception's single
   * marketing box), which then renders whole with no control.
   */
  collapsedTail?: string;
  /**
   * Marks the box whose state a `marketingSmsOptOut` write records. Boxes
   * without it are still collected and reported in the submission, but drive no
   * preference yet — the account/RCS box until GROWTH-2800's write lands.
   */
  drivesPreference?: boolean;
}

/**
 * The consent shown beside a phone field: the checkboxes rendered and the
 * `consentLanguage` key recorded for them, bound together in one object so the
 * audit trail can always name the exact copy the user saw.
 *
 * `version` names the **descriptions**. Revise copy by adding a new version and
 * pointing surfaces at it — never by editing an existing one, since historical
 * `Consent` rows still reference the key.
 */
export interface SmsConsentSpec {
  version: string;
  checkboxes: readonly SmsConsentCheckbox[];
}

/**
 * The value a `marketingSmsOptOut` write should record: the state of the box
 * marked `drivesPreference`. A spec is expected to mark one; a spec that marks
 * none reads as opted out, matching the customer service's own seeded default.
 */
export function resolveMarketingOptIn(
  spec: SmsConsentSpec,
  checked: Readonly<Record<string, boolean>>,
): boolean {
  return spec.checkboxes.some((box) => box.drivesPreference === true && checked[box.name] === true);
}

/** A description split into the part always on screen and the part folded away. */
export interface ConsentDescriptionParts {
  /** Always visible. The whole description when there is nothing to fold. */
  summary: string;
  /** The folded remainder, or `''` when the description renders whole. */
  detail: string;
}

/**
 * Splits a checkbox's description at its `collapsedTail`.
 *
 * Fails safe: a `collapsedTail` that is not a suffix of `description` (copy
 * edited on one side only) yields the whole description as `summary` with no
 * `detail`, so the user still sees every word the recorded version names. The
 * suffix relationship is a test's job to enforce, not a render-time throw — a
 * crashing auth form is worse than an unfolded paragraph.
 */
export function splitConsentDescription(box: SmsConsentCheckbox): ConsentDescriptionParts {
  const { description, collapsedTail } = box;
  if (
    collapsedTail == null ||
    collapsedTail.length === 0 ||
    collapsedTail.length >= description.length ||
    !description.endsWith(collapsedTail)
  ) {
    return { summary: description, detail: '' };
  }
  return {
    summary: description.slice(0, description.length - collapsedTail.length).trimEnd(),
    detail: collapsedTail,
  };
}

/**
 * Every box in the spec with its final state, so a submission reports the whole
 * set that was on screen rather than only the boxes the user touched.
 */
export function smsConsentValues(
  spec: SmsConsentSpec,
  checked: Readonly<Record<string, boolean>>,
): Record<string, boolean> {
  return Object.fromEntries(spec.checkboxes.map((box) => [box.name, checked[box.name] === true]));
}
